import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import { api } from "../services/api";
import { toast, ToastContainer } from "react-toastify";

const AllExamsLayer = () => {
  // State for exams
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedDate, setSelectedDate] = useState("");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [dateFilterType, setDateFilterType] = useState("single"); // 'single' or 'range'
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Fetch exams from API
  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true);
        const response = await api.exams.getAll();

        if (response.success) {
          // Transform the data to match our component's expected format
          const formattedExams = response.data.map((exam) => ({
            id: exam.id,
            title: exam.title,
            category: exam.difficulty || "Basic",
            questions: exam.totalQuestions || 0,
            duration: exam.duration,
            passPercentage: exam.passPercentage,
            createdAt: new Date(exam.createdAt).toISOString().split("T")[0],
            status: exam.isActive ? "Active" : "Inactive",
            description: exam.description || "",
          }));

          setExams(formattedExams);
        } else {
          setError(response.message || "Failed to fetch exams");
        }
      } catch (err) {
        setError("Error connecting to the server");
        console.error("Error fetching exams:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, []);

  // Delete exam function
  const handleDeleteExam = async (id) => {
    if (window.confirm("Are you sure you want to delete this exam?")) {
      try {
        setLoading(true);
        const response = await api.exams.delete(id);

        if (response.success) {
          setExams(exams.filter((exam) => exam.id !== id));
          toast.success("Exam deleted successfully");
        } else {
          toast.error(response.message || "Failed to delete exam");
        }
      } catch (err) {
        toast.error("Error connecting to the server");
        console.error("Error deleting exam:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  // Toggle status function
  const handleToggleStatus = async (id) => {
    try {
      const examToUpdate = exams.find((exam) => exam.id === id);
      if (!examToUpdate) return;

      setLoading(true);
      const newStatus = examToUpdate.status === "Active" ? false : true;

      const response = await api.exams.update(id, {
        isActive: newStatus,
      });

      if (response.success) {
        setExams(
          exams.map((exam) => {
            if (exam.id === id) {
              return {
                ...exam,
                status: newStatus ? "Active" : "Inactive",
              };
            }
            return exam;
          })
        );
        toast.success(
          `Exam ${newStatus ? "activated" : "deactivated"} successfully`
        );
      } else {
        toast.error(response.message || "Failed to update exam status");
      }
    } catch (err) {
      toast.error("Error connecting to the server");
      console.error("Error updating exam status:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle date selection
  const handleDateSelection = (e) => {
    setSelectedDate(e.target.value);
    setIsCalendarOpen(false);
  };

  // Handle date range selection
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    // If end date is set and is before start date, reset it
    if (endDate && new Date(e.target.value) > new Date(endDate)) {
      setEndDate("");
    }
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  // Switch between single date and date range
  const switchDateFilterType = (type) => {
    setDateFilterType(type);
    // Clear previous filter values
    setSelectedDate("");
    setStartDate("");
    setEndDate("");
  };

  // Clear date filters
  const clearDateFilter = () => {
    if (dateFilterType === "single") {
      setSelectedDate("");
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  // Get unique categories for the dropdown
  const categories = ["All", ...new Set(exams.map((exam) => exam.category))];

  // Filtered exams
  const filteredExams = exams.filter((exam) => {
    const matchesSearch = exam.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCategory =
      categoryFilter === "All" || exam.category === categoryFilter;
    const matchesStatus =
      statusFilter === "All" || exam.status === statusFilter;

    // Date filtering
    let matchesDate = true;

    if (dateFilterType === "single" && selectedDate) {
      const selectedDateObj = new Date(selectedDate);
      const examDateObj = new Date(exam.createdAt);

      // Match if the day, month and year are the same
      matchesDate =
        examDateObj.getDate() === selectedDateObj.getDate() &&
        examDateObj.getMonth() === selectedDateObj.getMonth() &&
        examDateObj.getFullYear() === selectedDateObj.getFullYear();
    } else if (dateFilterType === "range") {
      if (startDate && endDate) {
        const examDate = new Date(exam.createdAt);
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Set time to beginning/end of day for proper comparison
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        matchesDate = examDate >= start && examDate <= end;
      } else if (startDate) {
        // Only start date is set - show all exams after this date
        const examDate = new Date(exam.createdAt);
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        matchesDate = examDate >= start;
      } else if (endDate) {
        // Only end date is set - show all exams before this date
        const examDate = new Date(exam.createdAt);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        matchesDate = examDate <= end;
      }
    }

    return matchesSearch && matchesCategory && matchesStatus && matchesDate;
  });

  // Function to format a date range for display
  const formatDateRange = () => {
    if (startDate && endDate) {
      return `${new Date(startDate).toLocaleDateString()} - ${new Date(
        endDate
      ).toLocaleDateString()}`;
    } else if (startDate) {
      return `From ${new Date(startDate).toLocaleDateString()} onwards`;
    } else if (endDate) {
      return `Until ${new Date(endDate).toLocaleDateString()}`;
    }
    return "";
  };

  return (
    <div className="container-fluid">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between align-items-center">
              <h6 className="text-lg fw-semibold mb-0">All Exams</h6>
              <Link to="/exam" className="btn btn-primary btn-sm">
                <Icon icon="heroicons:plus-circle" className="me-2" />
                Create New Exam
              </Link>
            </div>
            <div className="card-body p-24">
              {/* Filters */}
              <div className="row mb-4">
                <div className="col-md-4 mb-3">
                  <div className="input-group">
                    <span className="input-group-text">
                      <Icon icon="ion:search-outline" />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search exams..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <select
                    className="form-select"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    {categories.map((category, index) => (
                      <option key={index} value={category}>
                        {category === "All" ? "All Categories" : category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Date Filter */}
              <div className="row mb-4">
                <div className="col-md-12">
                  <div className="card p-3 border-light-subtle">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Filter by Date</h6>
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className={`btn ${
                            dateFilterType === "single"
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => switchDateFilterType("single")}
                        >
                          Single Date
                        </button>
                        <button
                          type="button"
                          className={`btn ${
                            dateFilterType === "range"
                              ? "btn-primary"
                              : "btn-outline-primary"
                          }`}
                          onClick={() => switchDateFilterType("range")}
                        >
                          Date Range
                        </button>
                      </div>
                    </div>

                    {dateFilterType === "single" ? (
                      <div className="row">
                        <div className="col-md-9">
                          <div className="input-group">
                            <span className="input-group-text">
                              <Icon icon="uil:calendar-alt" />
                            </span>
                            <input
                              type="date"
                              className="form-control"
                              value={selectedDate}
                              onChange={handleDateSelection}
                            />
                          </div>
                        </div>
                        <div className="col-md-3">
                          <button
                            className="btn btn-outline-secondary w-100"
                            onClick={clearDateFilter}
                            disabled={!selectedDate}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-4 mb-2 mb-md-0">
                          <div className="input-group">
                            <span className="input-group-text">From</span>
                            <input
                              type="date"
                              className="form-control"
                              value={startDate}
                              onChange={handleStartDateChange}
                            />
                          </div>
                        </div>
                        <div className="col-md-4 mb-2 mb-md-0">
                          <div className="input-group">
                            <span className="input-group-text">To</span>
                            <input
                              type="date"
                              className="form-control"
                              value={endDate}
                              onChange={handleEndDateChange}
                              min={startDate}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <button
                            className="btn btn-outline-secondary w-100"
                            onClick={clearDateFilter}
                            disabled={!startDate && !endDate}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Active filters display */}
                    {((dateFilterType === "single" && selectedDate) ||
                      (dateFilterType === "range" &&
                        (startDate || endDate))) && (
                      <div className="mt-3">
                        <span className="badge bg-primary-soft text-primary-dark px-3 py-2">
                          <Icon
                            icon="uil:calendar-alt"
                            className="me-1"
                            width="18"
                          />
                          {dateFilterType === "single"
                            ? `Date: ${new Date(
                                selectedDate
                              ).toLocaleDateString()}`
                            : `Date Range: ${formatDateRange()}`}
                          <button
                            className="btn-close btn-close-sm ms-2"
                            onClick={clearDateFilter}
                            style={{ fontSize: "0.65em" }}
                          ></button>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Loading and error states */}
              {loading && (
                <div className="d-flex justify-content-center my-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {/* Exams Table */}
              {!loading && !error && filteredExams.length === 0 && (
                <div className="alert alert-info" role="alert">
                  No exams found matching your criteria.
                </div>
              )}

              {!loading && !error && filteredExams.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="bg-base">
                      <tr>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Title
                        </th>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Category
                        </th>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Questions
                        </th>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Duration
                        </th>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Pass %
                        </th>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Created
                        </th>
                        <th scope="col" className="px-3 py-2 fw-semibold">
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-2 fw-semibold text-center"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExams.map((exam) => (
                        <tr key={exam.id}>
                          <td className="px-3 py-2">{exam.title}</td>
                          <td className="px-3 py-2">{exam.category}</td>
                          <td className="px-3 py-2">{exam.questions}</td>
                          <td className="px-3 py-2">{exam.duration} mins</td>
                          <td className="px-3 py-2">{exam.passPercentage}%</td>
                          <td className="px-3 py-2">{exam.createdAt}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`badge ${
                                exam.status === "Active"
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                            >
                              {exam.status}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className={`btn btn-sm ${
                                  exam.status === "Active"
                                    ? "btn-warning"
                                    : "btn-success"
                                }`}
                                onClick={() => handleToggleStatus(exam.id)}
                                title={
                                  exam.status === "Active"
                                    ? "Deactivate"
                                    : "Activate"
                                }
                              >
                                <Icon
                                  icon={
                                    exam.status === "Active"
                                      ? "carbon:pause-filled"
                                      : "carbon:play-filled-alt"
                                  }
                                />
                              </button>
                              <Link
                                to={`/exam-results?examId=${exam.id}`}
                                className="btn btn-primary btn-sm"
                                title="Show Results"
                              >
                                <Icon icon="material-symbols:leaderboard-outline" />
                              </Link>
                              <Link
                                to={`/exam?id=${exam.id}`}
                                className="btn btn-info btn-sm"
                                title="Edit"
                              >
                                <Icon icon="akar-icons:edit" />
                              </Link>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteExam(exam.id)}
                                title="Delete"
                              >
                                <Icon icon="fluent:delete-20-filled" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllExamsLayer;
